import React, { useEffect, useMemo, useState } from "react";

import categoriesData from "../../Components/Allcategories/Allcategories";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { fetchUsers as fetchUsersThunk } from "../../redux/maindashboard/dashboardSlice";

/* =========================================================
   ICONS
   ========================================================= */

import {
  FaUserMd,
  FaChild,
  FaAmbulance,
  FaArrowRight,
  FaCheckCircle,
} from "react-icons/fa";

import {
  GiHeartOrgan,
  GiKidneys,
  GiStethoscope,
  GiDroplets,
  GiLotus,
  GiMeditation,
  GiNeedleDrill,
  GiYinYang,
  GiMedicines,
  GiSpiralArrow,
} from "react-icons/gi";

import {
  MdOutlineHealthAndSafety,
  MdLocalHospital,
  MdMedicalServices,
  MdOutlineMedicalServices,
} from "react-icons/md";

import { HiOutlineUserGroup } from "react-icons/hi";

import { IoMedical } from "react-icons/io5";

import { BsHeartPulse, BsPeopleFill } from "react-icons/bs";

import { BiTestTube } from "react-icons/bi";

/* =========================================================
   ICON MAP
   ========================================================= */

const iconMap = {
  "General Surgeons": <MdLocalHospital />,
  "Cardiothoracic Surgeons": <BsHeartPulse />,
  Neurosurgeons: <MdOutlineHealthAndSafety />,
  "Orthopedic Surgeons": <FaUserMd />,
  "Plastic Surgeons": <IoMedical />,
  "Pediatric Surgeons": <FaChild />,
  "Urological Surgeons": <GiKidneys />,
  "Gynecological Surgeons (Gynaecologists)": <HiOutlineUserGroup />,
  "Vascular Surgeons": <GiStethoscope />,
  "Colorectal Surgeons": <BsPeopleFill />,
  "Endocrine Surgeons": <MdOutlineHealthAndSafety />,
  "Trauma Surgeons": <FaUserMd />,
  "Transplant Surgeons": <GiHeartOrgan />,
  "Surgical Oncologists": <MdOutlineHealthAndSafety />,
  "Maxillofacial Surgeons": <FaUserMd />,
  "Otorhinolaryngological Surgeons (ENT Surgeons)": <FaUserMd />,
  "Ophthalmic Surgeons (Ophthalmologists)": <FaUserMd />,
  "Bariatric Surgeons": <FaUserMd />,
  "Cosmetic Surgeons": <IoMedical />,
  "Spinal Surgeons": <FaUserMd />,
  "Thoracic Surgeons": <GiHeartOrgan />,
  "Reconstructive Surgeons": <IoMedical />,
  "Oral Surgeons": <FaUserMd />,
  "Hand Surgeons": <FaUserMd />,
  "Gyneco-oncological Surgeons": <HiOutlineUserGroup />,
  "Interventional Surgeons": <GiStethoscope />,
  "Endoscopic Surgeons": <MdOutlineHealthAndSafety />,

  Ambulances: <FaAmbulance />,
  "Blood Banks": <GiDroplets />,
  "Diagnostic Centers": <MdOutlineMedicalServices />,
  Lab: <BiTestTube />,
  "Medical Equipments": <MdMedicalServices />,
  "Medical Shops": <IoMedical />,
  "Mental Health Services": <MdOutlineHealthAndSafety />,
  "Nursing Homes": <FaUserMd />,
  "Pharmaceutical Agencies": <GiStethoscope />,
  Radiology: <MdOutlineMedicalServices />,
  "Rehabilitation Centers": <MdMedicalServices />,
  "Vaccination Center": <BsHeartPulse />,

  Homeopathy: <GiMedicines />,
  Ayurveda: <GiLotus />,
  Naturopathy: <GiMeditation />,
  Acupuncture: <GiNeedleDrill />,
  Yoga: <GiYinYang />,
  "Herbal Medicine": <GiLotus />,
  "Traditional Chinese Medicine": <GiSpiralArrow />,
};

/* =========================================================
   CATEGORY ICON HELPER
   ========================================================= */

const getCategoryIcon = (name) => {
  return iconMap[name] || <MdMedicalServices />;
};

/* =========================================================
   IMAGE SKELETON
   ========================================================= */

const ImageSkeleton = () => {
  return (
    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
  );
};

/* =========================================================
   SUB CATEGORY CARD
   ========================================================= */

const SubCategoryList = ({
  categories,
  onSelect,
  userCounts,
  selectedMainCategory,
}) => {
  const [loadedImages, setLoadedImages] = useState({});

  const handleImageLoad = (name) => {
    setLoadedImages((prev) => ({
      ...prev,
      [name]: "loaded",
    }));
  };

  const handleImageError = (name) => {
    setLoadedImages((prev) => ({
      ...prev,
      [name]: "fallback",
    }));
  };

  /* =======================================================
     DETERMINE IMAGE FOLDER
     ======================================================= */

  let baseFolder = "Doctors";

  if (selectedMainCategory?.name === "Alternative Medicine") {
    baseFolder = "ALternative medicine";
  } else if (selectedMainCategory?.name === "Other Medical Services") {
    baseFolder = "Othermedical services";
  }

  /* =======================================================
     EMPTY CATEGORY
     ======================================================= */

  if (!categories?.length) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
        <MdMedicalServices className="mx-auto mb-3 text-4xl text-gray-300" />

        <p className="text-sm font-medium text-gray-500">
          No services available in this category.
        </p>
      </div>
    );
  }

  return (
    <div
      className="
        grid
        grid-cols-1
        gap-4
        sm:grid-cols-2
        md:gap-5
        lg:grid-cols-3
        xl:grid-cols-4
        2xl:gap-6
      "
    >
      {categories.map((subCategory, index) => {
        const name = subCategory?.name || "Medical Service";

        const imagePath = `/all/${baseFolder}/${encodeURIComponent(name)}.jpg`;

        const imageStatus = loadedImages[name];

        const count = userCounts?.[name] || 0;

        return (
          <article
            key={subCategory?.id || `${name}-${index}`}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(subCategory)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(subCategory);
              }
            }}
            className="
                group
                relative
                cursor-pointer
                overflow-hidden
                rounded-2xl
                border
                border-gray-100
                bg-white
                shadow-sm
                outline-none
                transition-all
                duration-300
                ease-out

                hover:-translate-y-1
                hover:shadow-xl
                hover:shadow-blue-100/70

                focus:ring-4
                focus:ring-blue-100

                animate-[fadeIn_0.5s_ease-out_both]
              "
            style={{
              animationDelay: `${index * 45}ms`,
            }}
          >
            {/* =================================================
                  IMAGE
                  ================================================= */}

            <div className="relative h-44 overflow-hidden bg-gray-100 sm:h-40">
              {/* Skeleton */}

              {!imageStatus && <ImageSkeleton />}

              {/* Image */}

              {imageStatus === "fallback" ? (
                <img
                  src="/fallback.jpg"
                  alt={name}
                  className="
                      h-full
                      w-full
                      object-cover
                      transition-transform
                      duration-500
                      group-hover:scale-105
                    "
                />
              ) : (
                <img
                  src={imagePath}
                  alt={name}
                  loading="lazy"
                  className={`
                      h-full
                      w-full
                      object-cover
                      transition-all
                      duration-500
                      group-hover:scale-110
                      ${imageStatus === "loaded" ? "opacity-100" : "opacity-0"}
                    `}
                  onLoad={() => handleImageLoad(name)}
                  onError={() => handleImageError(name)}
                />
              )}

              {/* =================================================
                    IMAGE OVERLAY
                    ================================================= */}

              <div
                className="
                    absolute
                    inset-0
                    bg-gradient-to-t
                    from-black/50
                    via-black/5
                    to-transparent
                    opacity-70
                    transition-opacity
                    duration-300
                    group-hover:opacity-90
                  "
              />

              {/* =================================================
                    AVAILABILITY BADGE
                    ================================================= */}

              <div className="absolute left-3 top-3">
                {count > 0 ? (
                  <div
                    className="
                        flex
                        items-center
                        gap-1.5
                        rounded-full
                        bg-white/95
                        px-2.5
                        py-1
                        text-[11px]
                        font-bold
                        text-green-600
                        shadow-sm
                        backdrop-blur
                      "
                  >
                    <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                    Available
                  </div>
                ) : (
                  <div
                    className="
                        rounded-full
                        bg-black/50
                        px-2.5
                        py-1
                        text-[11px]
                        font-medium
                        text-white
                        backdrop-blur
                      "
                  >
                    No providers
                  </div>
                )}
              </div>

              {/* =================================================
                    COUNT
                    ================================================= */}

              <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                <div className="max-w-[80%]">
                  <p className="text-xs font-medium text-white/80">MRHealth</p>

                  <h3 className="mt-0.5 line-clamp-2 text-base font-bold leading-tight text-white">
                    {name}
                  </h3>
                </div>

                {count > 0 && (
                  <div
                    className="
                        flex
                        h-9
                        min-w-9
                        items-center
                        justify-center
                        rounded-full
                        bg-white
                        px-2
                        text-xs
                        font-extrabold
                        text-blue-700
                        shadow-lg
                      "
                  >
                    {count}
                  </div>
                )}
              </div>
            </div>

            {/* =================================================
                  CARD CONTENT
                  ================================================= */}

            <div className="p-4">
              {/* Service title */}

              <div className="flex items-start gap-3">
                <div
                  className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-blue-50
                      text-lg
                      text-blue-600
                      transition-all
                      duration-300

                      group-hover:bg-blue-600
                      group-hover:text-white
                      group-hover:shadow-md
                    "
                >
                  {getCategoryIcon(name)}
                </div>

                <div className="min-w-0">
                  <h3
                    className="
                        line-clamp-2
                        text-sm
                        font-bold
                        leading-5
                        text-gray-800
                        transition-colors
                        group-hover:text-blue-700
                      "
                  >
                    {name}
                  </h3>

                  <p className="mt-0.5 text-xs font-medium text-gray-400">
                    Healthcare service
                  </p>
                </div>
              </div>

              {/* Description */}

              <p
                className="
                    mt-3
                    line-clamp-2
                    min-h-[40px]
                    text-xs
                    leading-5
                    text-gray-500
                    sm:text-sm
                  "
              >
                {subCategory?.description ||
                  "Find trusted healthcare professionals and services through MRHealth."}
              </p>

              {/* Bottom */}

              <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                <span
                  className={`text-xs font-bold ${
                    count > 0 ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  {count > 0
                    ? `${count} provider${count === 1 ? "" : "s"}`
                    : "No providers"}
                </span>

                <span
                  className="
                      flex
                      items-center
                      gap-1
                      text-xs
                      font-bold
                      text-blue-600
                      transition-all
                      duration-300
                      group-hover:gap-2
                    "
                >
                  Explore
                  <FaArrowRight className="text-[10px]" />
                </span>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};

/* =========================================================
   SKELETON SECTION
   ========================================================= */

const LoadingSkeleton = () => {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({
        length: 8,
      }).map((_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
        >
          <div className="h-40 animate-pulse bg-gray-200" />

          <div className="space-y-3 p-4">
            <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />

            <div className="h-3 w-full animate-pulse rounded bg-gray-100" />

            <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
};

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

const App = () => {
  const [userData, setUserData] = useState([]);

  const [loading, setLoading] = useState(true);

  const [fetchError, setFetchError] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const postcode = useSelector((state) => state.businessUserSearch.postcode);

  /* =======================================================
     FETCH USERS
     ======================================================= */

  useEffect(() => {
    /*
     * No postcode means there is no location
     * to search against.
     */
    if (!postcode) {
      setUserData([]);
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setFetchError(false);

        const res = await dispatch(
          fetchUsersThunk({
            postcode,
          }),
        );

        if (!mounted) {
          return;
        }

        const data = res?.payload?.users || [];

        setUserData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("❌ Fetch users error:", err);

        if (mounted) {
          setUserData([]);
          setFetchError(true);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      mounted = false;
    };
  }, [postcode, dispatch]);

  /* =======================================================
     USER COUNTS
     ======================================================= */

  const userCounts = useMemo(() => {
    const counts = {};

    (userData || []).forEach((user) => {
      const sub = user?.categoryPath?.[1];

      if (sub) {
        counts[sub] = (counts[sub] || 0) + 1;
      }
    });

    return counts;
  }, [userData]);

  /* =======================================================
     TOTAL PROVIDERS
     ======================================================= */

  const totalProviders = useMemo(() => {
    return Object.values(userCounts).reduce((total, count) => total + count, 0);
  }, [userCounts]);

  /* =======================================================
     LOADING
     ======================================================= */

  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-3 py-6 sm:px-5 sm:py-8 lg:px-8">
          {/* Header skeleton */}

          <div className="mb-8">
            <div className="h-8 w-64 animate-pulse rounded-lg bg-gray-200" />

            <div className="mt-3 h-4 w-96 max-w-full animate-pulse rounded bg-gray-100" />
          </div>

          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  /* =======================================================
     ERROR
     ======================================================= */

  if (fetchError) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-red-100 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
            <MdMedicalServices className="text-2xl" />
          </div>

          <h2 className="text-lg font-bold text-gray-800">
            Unable to load services
          </h2>

          <p className="mt-2 text-sm leading-6 text-gray-500">
            We couldn't load healthcare providers for your selected location.
            Please try again.
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="
              mt-5
              rounded-xl
              bg-blue-600
              px-5
              py-2.5
              text-sm
              font-bold
              text-white
              transition
              hover:bg-blue-700
            "
          >
            Try Again
          </button>
        </div>
      </main>
    );
  }

  /* =======================================================
     MAIN UI
     ======================================================= */

  return (
    <main className="min-h-screen w-full bg-gradient-to-b from-gray-50 via-white to-blue-50/40">
      {/* =====================================================
          PAGE CONTAINER
          ===================================================== */}

      <div className="container mx-auto w-full px-3 py-6 sm:px-5 sm:py-8 md:px-8 lg:px-10 xl:px-12">
        {/* ===================================================
            PAGE INTRO
            =================================================== */}

        <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />

              <span className="text-xs font-bold uppercase tracking-wider text-green-600">
                Healthcare services
              </span>
            </div>

            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl lg:text-4xl">
              Find the right healthcare
              <span className="text-blue-600"> for you</span>
            </h1>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500 sm:text-base">
              Explore doctors, medical services, diagnostic centers, alternative
              medicine and more near you.
            </p>
          </div>

          {/* =================================================
              PROVIDER COUNT
              ================================================= */}

          {totalProviders > 0 && (
            <div
              className="
                inline-flex
                w-fit
                items-center
                gap-2
                rounded-full
                border
                border-blue-100
                bg-blue-50
                px-4
                py-2
                text-xs
                font-bold
                text-blue-700
                shadow-sm
              "
            >
              <HiOutlineUserGroup className="text-base" />
              {totalProviders} healthcare providers available
            </div>
          )}
        </div>

        {/* ===================================================
            NO POSTCODE
            =================================================== */}

        {!postcode && (
          <div className="mb-8 rounded-2xl border border-blue-100 bg-blue-50 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                <MdOutlineHealthAndSafety />
              </div>

              <div>
                <h2 className="text-sm font-bold text-gray-800 sm:text-base">
                  Select your location
                </h2>

                <p className="mt-1 text-xs leading-5 text-gray-500 sm:text-sm">
                  Choose your city, area or pincode above to discover healthcare
                  providers near you.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================
            CATEGORY SECTIONS
            =================================================== */}

        <div className="space-y-10">
          {categoriesData.map((mainCategory, index) => {
            const services = mainCategory?.services || [];

            return (
              <section
                key={mainCategory?.id || `${mainCategory?.name}-${index}`}
                className="
                    animate-[fadeIn_0.6s_ease-out_both]
                  "
                style={{
                  animationDelay: `${index * 80}ms`,
                }}
              >
                {/* =================================================
                      SECTION HEADER
                      ================================================= */}

                <div className="mb-4 flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div
                      className="
                          flex
                          h-10
                          w-10
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          bg-gradient-to-br
                          from-blue-500
                          to-indigo-600
                          text-lg
                          text-white
                          shadow-md
                        "
                    >
                      {getCategoryIcon(services[0]?.name)}
                    </div>

                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-extrabold text-gray-900 sm:text-xl lg:text-2xl">
                        {mainCategory?.name}
                      </h2>

                      <p className="mt-0.5 text-xs text-gray-500 sm:text-sm">
                        {services.length} service
                        {services.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  {/* Decorative line */}

                  <div className="hidden flex-1 items-center gap-3 sm:flex">
                    <div className="h-px flex-1 bg-gradient-to-r from-blue-100 to-transparent" />
                  </div>
                </div>

                {/* =================================================
                      SERVICES
                      ================================================= */}

                <SubCategoryList
                  categories={services}
                  userCounts={userCounts}
                  selectedMainCategory={mainCategory}
                  onSelect={(subCat) => {
                    const matchedUsers = (userData || []).filter(
                      (user) => user?.categoryPath?.[1] === subCat?.name,
                    );

                    navigate(`/UserCard/${encodeURIComponent(subCat.name)}`, {
                      state: {
                        users: matchedUsers,
                      },
                    });
                  }}
                />
              </section>
            );
          })}
        </div>

        {/* ===================================================
            BOTTOM NOTE
            =================================================== */}

        <div className="mt-12 rounded-2xl border border-gray-100 bg-white p-5 text-center shadow-sm sm:p-6">
          <div className="mx-auto flex max-w-2xl flex-col items-center">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-green-50 text-green-600">
              <FaCheckCircle />
            </div>

            <h3 className="text-sm font-bold text-gray-800 sm:text-base">
              Healthcare made easier with MRHealth
            </h3>

            <p className="mt-1 text-xs leading-5 text-gray-500 sm:text-sm">
              Find healthcare professionals and services based on your location
              and connect with the right provider.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default App;
